/**
 * 課題管理の画面で使う、表示の決まり（時刻・伏せ字の札・検索語の色・選択肢の文言キー・保留の見直す日）。
 *
 * 時刻はすべて日本時間で出す。伏せ字（`[メール]` `[電話]` `[郵便番号]` `●●様`）は本文と見分けられるよう札にする。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の画面の表示の決まり（日本時間の時刻・伏せ字の札・検索語の色付け・選択肢の文言・保留の見直す日の候補）をまとめる
 * @graph-connects none
 */
import {
  addDays,
  formatJstDateTime,
  type IssueDisplayStatus,
  type IssuePriority,
  type IssueRejectReason,
  type IssueReviewWeekday,
  type IssueVerdict,
  type UnlinkedVoiceState,
} from '@cortex/domain-ac-mens-hub-api';

/**
 * 日本時間の今日（YYYY-MM-DD）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 日本時間の今日の日付を出す
 * @graph-connects formatJstDateTime [calls] 日本時間
 */
export function jstToday(now: Date): string {
  return formatJstDateTime(now.getTime()).slice(0, 10);
}

/**
 * 投稿日時・変更日時の表示。今日の分は「10:15」、今年の分は「11/12 10:15」、それより前は「2025/11/12」。
 * 時刻が無く日付だけ（旧表の声）なら、今年は「11/12」、それより前は「2025/11/12」。どちらも無ければ null。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の時刻を、今日・今年・それより前で書き分けて日本時間で表示する
 * @graph-connects formatJstDateTime [calls] 日本時間
 */
export function formatIssueTime(iso: string | null, date: string | null, now: Date): string | null {
  const today = jstToday(now);
  if (iso !== null) {
    const jst = formatJstDateTime(Date.parse(iso));
    const day = jst.slice(0, 10);
    const time = jst.slice(11, 16);
    if (day === today) return time;
    if (day.slice(0, 4) === today.slice(0, 4)) return `${day.slice(5).replace('-', '/')} ${time}`;
    return day.replace(/-/g, '/');
  }
  if (date === null) return null;
  if (date.slice(0, 4) === today.slice(0, 4)) return date.slice(5).replace('-', '/');
  return date.replace(/-/g, '/');
}

/**
 * 日付（YYYY-MM-DD）を「11/06」の形にする。年が違えば「2027/01/06」。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 見直す日などの日付を、今年なら月日だけで表示する
 * @graph-connects none
 */
export function formatShortDate(date: string, now: Date): string {
  return date.slice(0, 4) === jstToday(now).slice(0, 4)
    ? date.slice(5).replace('-', '/')
    : date.replace(/-/g, '/');
}

/**
 * 本文の1区切り。mask は伏せ字の札、hit は検索語に当たった部分。
 * @graph-connects none
 */
export interface TextSegment {
  text: string;
  mask: boolean;
  hit: boolean;
}

/**
 * 伏せ字の印。
 * @graph-connects none
 */
const MASK_TOKEN_RE = /(\[メール\]|\[電話\]|\[郵便番号\]|●●様|●●さま)/;

/**
 * 検索語（空白で区切った語）を、正規表現で使える形にする。空なら null。
 * @graph-connects none
 */
function queryPattern(query: string): RegExp | null {
  const words = query
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return words.length === 0 ? null : new RegExp(`(${words.join('|')})`, 'i');
}

/**
 * 本文を、伏せ字の札・検索語に当たった部分・それ以外に区切る。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 声や課題の文を、伏せ字の札と検索語の色付けのために区切る
 * @graph-connects none
 */
export function splitIssueText(text: string, query: string): TextSegment[] {
  const pattern = queryPattern(query);
  const segments: TextSegment[] = [];
  for (const part of text.split(MASK_TOKEN_RE)) {
    if (part === '') continue;
    if (MASK_TOKEN_RE.test(part)) {
      segments.push({ text: part, mask: true, hit: false });
      continue;
    }
    if (pattern === null) {
      segments.push({ text: part, mask: false, hit: false });
      continue;
    }
    for (const piece of part.split(pattern)) {
      if (piece === '') continue;
      segments.push({ text: piece, mask: false, hit: pattern.test(piece) });
    }
  }
  return segments;
}

/**
 * 見せる状態の文言キー。
 * @graph-connects none
 */
export const ISSUE_STATUS_LABEL_KEYS: Record<IssueDisplayStatus, string> = {
  awaiting_decision: 'issueMgmt.status.awaitingDecision',
  in_progress: 'issueMgmt.status.inProgress',
  on_hold: 'issueMgmt.status.onHold',
  resolved: 'issueMgmt.status.resolved',
  rejected: 'issueMgmt.status.rejected',
};

/**
 * 判定の文言キー（変更履歴と競合の知らせで使う）。
 * @graph-connects none
 */
export const ISSUE_VERDICT_LABEL_KEYS: Record<IssueVerdict, string> = {
  pending: 'issueMgmt.verdict.pending',
  accepted: 'issueMgmt.verdict.accepted',
  on_hold: 'issueMgmt.verdict.onHold',
  rejected: 'issueMgmt.verdict.rejected',
  merged: 'issueMgmt.verdict.merged',
};

/**
 * 見せる状態の札の色（判定待ち＝黄・対応中＝藍・解決済み＝緑・保留と対応しない＝灰）。
 * @graph-connects none
 */
export const ISSUE_STATUS_VARIANTS: Record<
  IssueDisplayStatus,
  'warning' | 'info' | 'success' | 'default'
> = {
  awaiting_decision: 'warning',
  in_progress: 'info',
  on_hold: 'default',
  resolved: 'success',
  rejected: 'default',
};

/**
 * 優先度の文言キー。
 * @graph-connects none
 */
export const ISSUE_PRIORITY_LABEL_KEYS: Record<IssuePriority, string> = {
  high: 'issueMgmt.priority.high',
  medium: 'issueMgmt.priority.medium',
  low: 'issueMgmt.priority.low',
};

/**
 * 「対応しない」の理由の文言キー（BR-07 の6つ）。
 * @graph-connects none
 */
export const ISSUE_REJECT_REASON_LABEL_KEYS: Record<IssueRejectReason, string> = {
  as_designed: 'issueMgmt.rejectReason.asDesigned',
  cost_outweighs: 'issueMgmt.rejectReason.costOutweighs',
  not_reproducible: 'issueMgmt.rejectReason.notReproducible',
  policy: 'issueMgmt.rejectReason.policy',
  resolved_elsewhere: 'issueMgmt.rejectReason.resolvedElsewhere',
  other: 'issueMgmt.rejectReason.other',
};

/**
 * 週次の曜日の文言キー。
 * @graph-connects none
 */
export const ISSUE_WEEKDAY_LABEL_KEYS: Record<IssueReviewWeekday, string> = {
  mon: 'issueMgmt.weekday.mon',
  tue: 'issueMgmt.weekday.tue',
  wed: 'issueMgmt.weekday.wed',
  thu: 'issueMgmt.weekday.thu',
  fri: 'issueMgmt.weekday.fri',
};

/**
 * 手で入れた声の発生元の文言キー。
 * @graph-connects none
 */
export const ISSUE_VOICE_SOURCE_LABEL_KEYS: Record<'phone' | 'meeting' | 'chat' | 'other', string> =
  {
    phone: 'issueMgmt.voiceSource.phone',
    meeting: 'issueMgmt.voiceSource.meeting',
    chat: 'issueMgmt.voiceSource.chat',
    other: 'issueMgmt.voiceSource.other',
  };

/**
 * 課題に紐づいていない声の状態の文言キー。
 * @graph-connects none
 */
export const ISSUE_UNLINKED_STATE_LABEL_KEYS: Record<UnlinkedVoiceState, string> = {
  legacy_done: 'issueMgmt.unlinkedState.legacyDone',
  not_issue: 'issueMgmt.unlinkedState.notIssue',
  excluded: 'issueMgmt.unlinkedState.excluded',
  unassigned: 'issueMgmt.unlinkedState.unassigned',
  later: 'issueMgmt.unlinkedState.later',
};

/**
 * 案件の紐づけの状態の文言キー。
 * @graph-connects none
 */
export const ISSUE_CASE_STATE_LABEL_KEYS: Record<'pending' | 'confirmed' | 'needs_check', string> =
  {
    pending: 'issueMgmt.caseState.pending',
    confirmed: 'issueMgmt.caseState.confirmed',
    needs_check: 'issueMgmt.caseState.needsCheck',
  };

/**
 * 工数の目安の文言キー。
 * @graph-connects none
 */
export const ISSUE_EFFORT_LABEL_KEYS: Record<'small' | 'medium' | 'large', string> = {
  small: 'issueMgmt.effort.small',
  medium: 'issueMgmt.effort.medium',
  large: 'issueMgmt.effort.large',
};

/**
 * 競合を知らせるときの項目名の文言キー（課題の項目・判定・設定）。
 * @graph-connects none
 */
export const ISSUE_FIELD_LABEL_KEYS: Record<string, string> = {
  title: 'issueMgmt.field.title',
  whatText: 'issueMgmt.field.what',
  whatMark: 'issueMgmt.field.what',
  guidanceText: 'issueMgmt.field.guidance',
  guidanceMark: 'issueMgmt.field.guidance',
  fixedText: 'issueMgmt.field.fixed',
  fixedMark: 'issueMgmt.field.fixed',
  domain: 'issueMgmt.field.domain',
  targetMonth: 'issueMgmt.field.targetMonth',
  effortEstimate: 'issueMgmt.field.effort',
  themeIds: 'issueMgmt.field.themes',
  relatedLinks: 'issueMgmt.field.links',
  memo: 'issueMgmt.field.memo',
  verdict: 'issueMgmt.field.verdict',
  assignee: 'issueMgmt.field.assignee',
  priority: 'issueMgmt.field.priority',
  devRequired: 'issueMgmt.field.devRequired',
  holdUntil: 'issueMgmt.field.holdUntil',
  rejectReason: 'issueMgmt.field.rejectReason',
  verdictNote: 'issueMgmt.field.verdictNote',
  triageOwner: 'issueMgmt.field.triageOwner',
  judge: 'issueMgmt.field.judge',
  reviewWeekday: 'issueMgmt.field.reviewWeekday',
  aiSuggestions: 'issueMgmt.field.aiSuggestions',
  manualProgress: 'issueMgmt.field.progress',
  doneNote: 'issueMgmt.field.doneNote',
  resolvedAt: 'issueMgmt.field.resolvedAt',
  unresolvedAt: 'issueMgmt.field.unresolvedAt',
  mergedInto: 'issueMgmt.field.mergedInto',
  migrationNote: 'issueMgmt.field.migrationNote',
  legacyIds: 'issueMgmt.field.legacyIds',
  state: 'issueMgmt.field.caseState',
};

/**
 * 開発しない課題の進捗の文言キー（変更履歴で使う）。
 * @graph-connects none
 */
export const ISSUE_PROGRESS_LABEL_KEYS: Record<'not_started' | 'in_progress' | 'done', string> = {
  not_started: 'issueMgmt.progress.notStarted',
  in_progress: 'issueMgmt.progress.inProgress',
  done: 'issueMgmt.progress.done',
};

/**
 * 保留の見直す日の候補の日付（日本時間の今日から数える）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 保留の見直す日の候補（2週間後・30日後・60日後）を日本時間の今日から出す
 * @graph-connects addDays [calls] 日付をずらす
 */
export function holdPresetDate(now: Date, days: number): string {
  return addDays(jstToday(now), days);
}

/**
 * 選択肢で持つ項目の値の文言キー（変更履歴と競合の知らせで、内部の値を文言にする）。
 * @graph-connects ISSUE_VERDICT_LABEL_KEYS [references] 判定
 * @graph-connects ISSUE_PRIORITY_LABEL_KEYS [references] 優先度
 * @graph-connects ISSUE_REJECT_REASON_LABEL_KEYS [references] 対応しないの理由
 * @graph-connects ISSUE_WEEKDAY_LABEL_KEYS [references] 週次の曜日
 * @graph-connects ISSUE_EFFORT_LABEL_KEYS [references] 工数の目安
 * @graph-connects ISSUE_PROGRESS_LABEL_KEYS [references] 開発しない課題の進捗
 * @graph-connects ISSUE_CASE_STATE_LABEL_KEYS [references] 案件の紐づけの状態
 */
export const ISSUE_VALUE_LABEL_KEYS: Record<string, Record<string, string>> = {
  verdict: ISSUE_VERDICT_LABEL_KEYS,
  priority: ISSUE_PRIORITY_LABEL_KEYS,
  rejectReason: ISSUE_REJECT_REASON_LABEL_KEYS,
  reviewWeekday: ISSUE_WEEKDAY_LABEL_KEYS,
  effortEstimate: ISSUE_EFFORT_LABEL_KEYS,
  manualProgress: ISSUE_PROGRESS_LABEL_KEYS,
  state: ISSUE_CASE_STATE_LABEL_KEYS,
};
